import React, { useState, useCallback, useEffect } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import moment from 'moment';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';
import Activity from '../../components/Activity';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';

export default function CustomerPayMethod({ customer }) {
  const routeName = '/customerspaymentmethods';
  const dataName = 'Pagamentos';
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const [customerEdit, setCustomer] = useState(customer);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentRecurring, setPaymentRecurring] = useState(0);
  const recurringPayment = [
    { value: '', label: '-- Selecione --' },
    { value: 1, label: 'Habilitado' },
    { value: 0, label: 'Desabilitado' },
  ];
  const paymentTypes = {
    DBA: 'Débito automático',
  };

  const [formEditPay, setFormEditPay] = useState({
    payment_method_id: '',
    due_date: '',
  });

  const validationSchemaPay = Yup.object().shape({
    payment_method_id: Yup.number()
      .typeError('Deve ser numérico')
      .required('Convênio de pagamento obrigatório.'),
    due_date: Yup.date()
      .typeError('Deve ser uma data válida')
      .required('Data de vencimento obrigatória.'),
    recurring_payment: Yup.number()
      .typeError('Deve ser numérico')
      .required('Cobrança recorrente obrigatória.'),
  });

  const handleFieldChangePay = useCallback((fieldName, value) => {
    let newValue = false;

    if (fieldName === 'due_date') {
      let validateDate = moment(value, 'DD/MM/YYYY', true);

      if (!validateDate.isValid()) {
        validateDate = moment(value, 'YYYY-MM-DD', true);
      }

      newValue = validateDate.isValid()
        ? validateDate.format('YYYY-MM-DD 12:00:00')
        : null;
    }

    setFormEditPay((prevValues) => ({
      ...prevValues,
      [fieldName]: newValue === false ? value : newValue,
    }));
  }, []);

  const fieldsPay = [
    {
      name: 'payment_method_id',
      id: 'payment_method_id',
      label: 'Convênio de pagamento',
      type: 'select',
      options: paymentMethods,
      onChange: (e) =>
        handleFieldChangePay('payment_method_id', e.target.value),
      colSize: 4,
      additionalProps: {
        className:
          formEditPay.id && formEditPay.status === 1 ? 'click-disabled' : '',
      },
      group: 'Pagamento',
    },
    {
      name: 'due_date',
      id: 'due_date',
      label: 'Data de vencimento',
      type: 'date',
      onChange: (e) => handleFieldChangePay('due_date', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Pagamento',
    },
    {
      name: 'recurring_payment',
      id: 'recurring_payment',
      label: 'Cobrança recorrente',
      type: 'select',
      options: recurringPayment,
      onChange: (e) =>
        handleFieldChangePay('recurring_payment', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Pagamento',
    },
  ];

  if (formEditPay.id && formEditPay.status === 0) {
    fieldsPay.push({
      name: 'gen_pay_api',
      id: 'gen_pay_api',
      label: 'Gerar nova cobrança ao atualizar',
      type: 'select',
      options: [
        { label: 'Não', value: 0 },
        { label: 'Sim', value: 1 },
      ],
      onChange: (e) => handleFieldChangePay('gen_pay_api', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Pagamento',
    });
  }

  const getPaymentHistory = useCallback(async () => {
    const historyActivity = [];
    if (customerEdit.CustomerPaymentHistories) {
      customerEdit.CustomerPaymentHistories.forEach((value) => {
        let coloTextClass = 'text-gray';
        if (value.status === 0) {
          coloTextClass = 'text-red';
        }
        if (value.status === 1) {
          coloTextClass = 'text-green';
        }
        historyActivity.push({
          name: <b className={coloTextClass}>{value.status_description}</b>,
          text: (
            <div>
              R$ {value.value.toLocaleString('pt-BR')} -{' '}
              {paymentTypes[value.payment_type]}
            </div>
          ),
          time: moment.utc(value.payment_date).format('DD/MM/YYYY'),
          icon: 'dollar-badge-1',
        });
      });
    }
    setPaymentHistory(historyActivity);
  }, [customerEdit, paymentTypes]);

  const getCustomer = useCallback(async () => {
    try {
      const request = await axios.get(`customers/${customerEdit.id}`);
      if (request.data.CustomerPaymentMethod) {
        setFormEditPay({ ...request.data.CustomerPaymentMethod });
        setPaymentRecurring(
          request.data.CustomerPaymentMethod.recurring_payment
        );
      } else {
        setFormEditPay({
          payment_method_id: '',
          due_date: '',
          recurring_payment: '',
        });
      }
      setCustomer(request.data);
    } catch (err) {
      setApiErrors(err);
    }
  }, [customerEdit]);

  const cancelPayment = useCallback(
    async (id) => {
      try {
        setIsLoading(true);
        await axios.delete(`${routeName}/${id}`);
        await getCustomer();
        notification(
          `Solicitação de cancelamento enviada com sucesso`,
          'success'
        );
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, notification]
  );

  const getPaymentMethods = useCallback(async () => {
    try {
      const optionsPayments = [{ value: '', label: '-- Selecione --' }];
      const request = await axios.get(`paymentmethods`);

      if (request) {
        request.data.forEach((data) => {
          optionsPayments.push({
            value: data.id,
            label: `${data.name} - ${data.PaymentType.name}`,
          });
        });
      }

      setPaymentMethods(optionsPayments);
    } catch (err) {
      setApiErrors(err);
    }
  }, []);

  const handleSubmitPay = useCallback(async () => {
    try {
      setIsLoading(true);

      let action = 'atualizado';
      if (formEditPay.id) {
        await axios.put(`${routeName}/${formEditPay.id}`, formEditPay);
      } else {
        await axios.post(routeName, {
          customer_id: customerEdit.id,
          ...formEditPay,
        });
        action = 'cadastrado';
      }

      await getCustomer();
      notification(`Pagamento ${action} com sucesso`, 'success');
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  }, [getCustomer, formEditPay, notification, customerEdit]);

  useEffect(() => {
    if (customerEdit.CustomerPaymentMethod) {
      customerEdit.CustomerPaymentMethod.due_date = moment
        .utc(customerEdit.CustomerPaymentMethod.due_date)
        .format('YYYY-MM-DD');
      setFormEditPay({ ...customerEdit.CustomerPaymentMethod });
      setPaymentRecurring(customerEdit.CustomerPaymentMethod.recurring_payment);
    }
    getPaymentMethods();
    getPaymentHistory();
    setDataLoaded(true);
  }, []);

  useEffect(() => {
    getPaymentHistory();
  }, [customerEdit]);

  return (
    <Row>
      <Col lg={12}>
        <Card className="mb-4">
          <Loading isLoading={isLoading} />
          <ErrorProcessor error={apiErrors} />
          <Card.Header>
            <h4 className="card-heading">Agendar {dataName}</h4>
          </Card.Header>
          <Card.Body>
            {dataLoaded && formEditPay.id ? (
              <div>
                {' '}
                <i>Situação atual com o convênio :</i>{' '}
                <b
                  className={
                    // eslint-disable-next-line no-nested-ternary
                    formEditPay.status === 1
                      ? 'text-success'
                      : paymentRecurring === 1
                        ? 'text-warning'
                        : 'text-danger'
                  }
                >
                  {
                    // eslint-disable-next-line no-nested-ternary
                    formEditPay.status === 1
                      ? 'Pagamento em processamento'
                      : paymentRecurring === 1
                        ? 'Cobrança recorrente em processamento'
                        : 'Nenhum pagamento programado'
                  }
                </b>
              </div>
            ) : null}
            {dataLoaded && formEditPay.id && formEditPay.status === 1 ? (
              <ButtonModal
                onConfirm={() => cancelPayment(formEditPay.id)}
                message={`Confirmar solicitação de cancelamento do pagamento agendado do cliente ${customerEdit.name} ${customerEdit.lastname}?`}
                buttonProps={{
                  className: 'float-end',
                  variant: 'danger',
                  title: 'Confirmar',
                }}
                textModal={{
                  title: `Cancelar pagamento agendado`,
                }}
              >
                Solicitar cancelamento
              </ButtonModal>
            ) : null}
            {dataLoaded ? (
              <Forms
                initialValues={formEditPay}
                validationSchema={validationSchemaPay}
                onSubmit={handleSubmitPay}
                fields={fieldsPay}
                btnSubmit={{
                  variant: formEditPay.id ? 'primary' : 'success',
                  type: 'submit',
                  text: formEditPay.id ? 'Atualizar' : 'Cadastrar',
                  className: 'text-white',
                }}
              />
            ) : null}
          </Card.Body>
        </Card>
      </Col>
      <Col lg={12}>
        <Activity
          styleCard={{ maxHeight: '500px' }}
          classCard="overflow-auto"
          title="Histórico de pagamentos"
          data={paymentHistory}
        />
      </Col>
    </Row>
  );
}

CustomerPayMethod.propTypes = {
  customer: PropTypes.shape({}).isRequired,
};
